import React from 'react';
import { motion } from 'framer-motion';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Target,
  Trophy,
  AlertCircle,
  Info,
  Percent
} from 'lucide-react';

const TransactionWithCommission = ({ transaction, index = 0, showUserInfo = true }) => {
  const formatCurrency = (amount) => {
    return `$${Number(amount || 0).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CO', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'win':
        return Trophy;
      case 'bet':
        return Target;
      case 'deposit':
        return TrendingUp;
      case 'withdrawal':
        return TrendingDown;
      default:
        return DollarSign;
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'win':
        return 'success';
      case 'bet':
        return 'warning';
      case 'deposit':
        return 'primary';
      case 'withdrawal':
        return 'danger';
      default:
        return 'gray';
    }
  };

  const Icon = getTransactionIcon(transaction.type);
  const color = getTransactionColor(transaction.type);
  const isWin = transaction.type === 'win';

  const platformAmount = parseFloat(transaction.commission_amount) || 0;
  const potAmount = parseFloat(transaction.pot_commission_amount) || 0;
  const referralAmount = parseFloat(transaction.referral_commission_amount) || 0;
  const totalCommission = platformAmount + potAmount + referralAmount;
  const hasCommission = isWin && totalCommission > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="bg-white dark:bg-dark-900 rounded-xl border border-gray-200 dark:border-dark-700 overflow-hidden"
    >
      <div className="p-4">
        {/* Header: Icon + Title */}
        <div className="flex items-start space-x-3 mb-3">
          <div className={`p-3 flex-shrink-0 rounded-xl bg-${color}-100 dark:bg-${color}-900/30`}>
            <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
          </div>

          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
              {transaction.type === 'win' ? '🏆 Ganancia por Empate' :
               transaction.type === 'bet' ? '🎯 Apuesta Realizada' :
               transaction.type === 'deposit' ? '💰 Depósito' :
               transaction.type === 'withdrawal' ? '💸 Retiro' : 'Transacción'}
            </h4>
            {hasCommission && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                <Percent className="w-3 h-3 mr-1" />
                {transaction.commission_rate}% comisión
              </span>
            )}
          </div>
        </div>

        {/* Transaction Info */}
        <div className="space-y-1 mb-3">
          {showUserInfo && transaction.full_name && (
            <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
              Cliente: <span className="font-medium">{transaction.full_name}</span>
            </p>
          )}
          {transaction.event_name && (
            <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
              Evento: <span className="font-medium">{transaction.event_name}</span>
              {transaction.step_number && ` - Paso ${transaction.step_number}`}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-500">
            {formatDate(transaction.created_at)}
          </p>
        </div>

        {/* Amounts Section */}
        {hasCommission ? (
          <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-dark-700">
            {/* Ganancia Bruta */}
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 dark:text-gray-400">Bruto:</span>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {formatCurrency(transaction.gross_amount)}
              </span>
            </div>

            {/* Comisión Plataforma */}
            {platformAmount > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-purple-600 dark:text-purple-400">
                  Plataforma{transaction.commission_rate ? ` (${transaction.commission_rate}%)` : ''}:
                </span>
                <span className="text-sm font-semibold text-purple-600 dark:text-purple-400">
                  -{formatCurrency(platformAmount)}
                </span>
              </div>
            )}

            {/* Comisión Pozos */}
            {potAmount > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-amber-600 dark:text-amber-400">Pozos:</span>
                <span className="text-sm font-semibold text-amber-600 dark:text-amber-400">
                  -{formatCurrency(potAmount)}
                </span>
              </div>
            )}

            {/* Comisión Referido (solo si aplica) */}
            {referralAmount > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-sky-600 dark:text-sky-400">Referido:</span>
                <span className="text-sm font-semibold text-sky-600 dark:text-sky-400">
                  -{formatCurrency(referralAmount)}
                </span>
              </div>
            )}

            {/* Pago al Cliente */}
            <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-dark-700">
              <span className="text-xs text-success-600 dark:text-success-400 font-medium">Cliente (neto):</span>
              <span className="text-lg font-bold text-success-600 dark:text-success-400">
                {formatCurrency(transaction.amount)}
              </span>
            </div>
          </div>
        ) : (
          <div className="pt-3 border-t border-gray-200 dark:border-dark-700">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 dark:text-gray-400">Monto:</span>
              <span className={`text-lg font-bold ${
                transaction.type === 'win' || transaction.type === 'deposit'
                  ? 'text-success-600 dark:text-success-400'
                  : 'text-gray-900 dark:text-gray-100'
              }`}>
                {transaction.type === 'bet' ? '-' : '+'}{formatCurrency(Math.abs(transaction.amount))}
              </span>
            </div>
          </div>
        )}

        {/* Descripción */}
        {transaction.description && (
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-dark-800">
            <p className="text-xs text-gray-600 dark:text-gray-400 italic break-words">
              {transaction.description}
            </p>
          </div>
        )}
      </div>

      {/* Resumen de comisión destacado */}
      {hasCommission && platformAmount > 0 && (
        <div className="px-4 py-2 bg-purple-50 dark:bg-purple-900/10 border-t border-purple-100 dark:border-purple-900/30">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs">
            <div className="flex items-center space-x-2 text-purple-700 dark:text-purple-300">
              <Info className="w-3 h-3 flex-shrink-0" />
              <span className="font-medium">
                Comisión plataforma: {transaction.commission_rate}% de la ganancia
              </span>
            </div>
            <span className="font-semibold text-purple-600 dark:text-purple-400">
              +{formatCurrency(platformAmount)} para la casa
            </span>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default TransactionWithCommission;
